import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IDataNoPagination } from '../types/shared';

export interface ICompanyField {
  id: number;
  campo: string;
  tipo: 'select' | 'text' | 'number';
  dt_exclusao_logica?: string | null;
}

// Formato retornado pela API GET /empresas/{id}/valores/
export interface ICompanyFieldValue {
  id: number;
  campo: ICompanyField; // A API retorna o campo como objeto
  value: string;
}

// Formato simplificado que vem na listagem de empresas (campos_valores)
export interface ICompanyFieldValueSimple {
  campo: string;
  value: string;
}

export interface ICreateFieldPayload {
  campo: string;
  tipo: 'select' | 'text';
}

export interface ISetFieldValuePayload {
  companyId: number;
  campo: string;
  value: string;
}

// Payload para atribuir um campo do tipo select diretamente a uma empresa (sem valor)
export interface ISetFieldPayload {
  empresaId: number;
  campoId: number;
}

// Payload para remover associação de campo select de uma empresa
export interface IDeleteFieldPayload {
  empresaId: number;
  campoId: number;
}

// Formato retornado pela API GET /campo/set/{empresa_id}/
export interface ICompanyFieldAssociation {
  id: number;
  campo: ICompanyField;
}

// Payload para atualizar um campo existente
export interface IUpdateFieldPayload {
  campo: string;
  novoNome?: string;
  tipo?: 'select' | 'text';
}

// Interface para órfãos (valores ou sets cujo campo foi excluído)
export interface IOrphanValue {
  id: number;
  empresa_id: number;
  empresa_nome?: string;
  campo_id: number;
  value?: string;
}

export interface IOrphanSet {
  id: number;
  empresa_id: number;
  empresa_nome?: string;
  campo_id: number;
}

export interface IOrphansSummary {
  valores_orfaos: number;
  sets_orfaos: number;
  total: number;
}

export const useCompanyFields = () => {
  // Buscar todos os campos disponíveis
  const getFields = async (
    key?: QueryFunctionContext,
  ): Promise<IDataNoPagination<ICompanyField[]>> => {
    const params = key?.queryKey?.[1] as { incluir_excluidos?: boolean } | undefined;
    const res = await api.get<IDataNoPagination<ICompanyField[]>>('/api/v1/empresas/campos/', {
      params: params || {},
    });
    return res.data;
  };

  // Criar um novo campo
  const createField = async (
    data: ICreateFieldPayload,
  ): Promise<IDataNoPagination<ICompanyField>> => {
    const res = await api.post<IDataNoPagination<ICompanyField>>('/api/v1/empresas/campos/', data);
    return res.data;
  };

  // Buscar valores de campos de uma empresa específica
  const getCompanyFieldValues = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<ICompanyFieldValue[]>> => {
    const { companyId, incluirExcluidos } = key.queryKey[1] as {
      companyId: number;
      incluirExcluidos?: boolean;
    };
    const res = await api.get<IDataNoPagination<ICompanyFieldValue[]>>(
      `/api/v1/empresas/${companyId}/valores/`,
      { params: incluirExcluidos ? { incluir_excluidos: incluirExcluidos } : {} },
    );
    return res.data;
  };

  // Atribuir/atualizar valor de um campo para uma empresa
  const setCompanyFieldValue = async (
    data: ISetFieldValuePayload,
  ): Promise<IDataNoPagination<ICompanyFieldValue>> => {
    const res = await api.post<IDataNoPagination<ICompanyFieldValue>>(
      `/api/v1/empresas/${data.companyId}/valores/`,
      { campo: data.campo, value: data.value },
    );
    return res.data;
  };

  // Deletar somente o campo (mantém os valores)
  const deleteField = async (campo: string): Promise<void> => {
    await api.delete(`/api/v1/empresas/campos/${campo}/`);
  };

  // Deletar o campo E todos os valores de todas as empresas (cascade)
  const deleteFieldCascade = async (campo: string): Promise<void> => {
    await api.delete(`/api/v1/empresas/campos/${campo}/cascade/`);
  };

  // Deletar apenas o valor de um campo de uma empresa específica
  const deleteCompanyFieldValue = async (data: {
    companyId: number;
    campo: string;
  }): Promise<void> => {
    await api.delete(`/api/v1/empresas/${data.companyId}/valores/${data.campo}/`);
  };

  // Atribuir um campo do tipo select diretamente a uma empresa (sem valor)
  // POST /api/v1/empresas/campo/set/
  const setCompanyField = async (
    data: ISetFieldPayload,
  ): Promise<IDataNoPagination<ICompanyFieldValue>> => {
    const res = await api.post<IDataNoPagination<ICompanyFieldValue>>(
      '/api/v1/empresas/campo/set/',
      { empresa_id: data.empresaId, campo_id: data.campoId },
    );
    return res.data;
  };

  // Listar campos do tipo select associados a uma empresa
  // GET /api/v1/empresas/campo/set/{empresa_id}/
  const getCompanyFieldAssociations = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<ICompanyFieldAssociation[]>> => {
    const { empresaId } = key.queryKey[1] as { empresaId: number };
    const res = await api.get<IDataNoPagination<ICompanyFieldAssociation[]>>(
      `/api/v1/empresas/campo/set/${empresaId}/`,
    );
    return res.data;
  };

  // Remover associação de campo select de uma empresa
  // DELETE /api/v1/empresas/campo/set/
  const deleteCompanyFieldAssociation = async (data: IDeleteFieldPayload): Promise<void> => {
    await api.delete('/api/v1/empresas/campo/set/', {
      data: { empresa_id: data.empresaId, campo_id: data.campoId },
    });
  };

  // Atualizar um campo existente
  // PUT /api/v1/empresas/campos/{campo}/
  const updateField = async (
    data: IUpdateFieldPayload,
  ): Promise<IDataNoPagination<ICompanyField>> => {
    const payload: Record<string, string> = {};
    if (data.novoNome) payload.campo = data.novoNome;
    if (data.tipo) payload.tipo = data.tipo;
    const res = await api.put<IDataNoPagination<ICompanyField>>(
      `/api/v1/empresas/campos/${data.campo}/`,
      payload,
    );
    return res.data;
  };

  // Listar valores órfãos (cujo campo foi excluído)
  // GET /api/v1/empresas/orfaos/valores/
  const listOrphanValues = async (): Promise<IDataNoPagination<IOrphanValue[]>> => {
    const res = await api.get<IDataNoPagination<IOrphanValue[]>>(
      '/api/v1/empresas/orfaos/valores/',
    );
    return res.data;
  };

  // Listar sets órfãos (cujo campo foi excluído)
  // GET /api/v1/empresas/orfaos/sets/
  const listOrphanSets = async (): Promise<IDataNoPagination<IOrphanSet[]>> => {
    const res = await api.get<IDataNoPagination<IOrphanSet[]>>('/api/v1/empresas/orfaos/sets/');
    return res.data;
  };

  // Limpar valores órfãos
  // DELETE /api/v1/empresas/orfaos/valores/
  const clearOrphanValues = async (): Promise<void> => {
    await api.delete('/api/v1/empresas/orfaos/valores/');
  };

  // Limpar sets órfãos
  // DELETE /api/v1/empresas/orfaos/sets/
  const clearOrphanSets = async (): Promise<void> => {
    await api.delete('/api/v1/empresas/orfaos/sets/');
  };

  // Limpar todos os órfãos (valores + sets)
  // DELETE /api/v1/empresas/orfaos/
  const clearAllOrphans = async (): Promise<void> => {
    await api.delete('/api/v1/empresas/orfaos/');
  };

  return {
    getFields,
    createField,
    getCompanyFieldValues,
    setCompanyFieldValue,
    deleteField,
    deleteFieldCascade,
    deleteCompanyFieldValue,
    setCompanyField,
    getCompanyFieldAssociations,
    deleteCompanyFieldAssociation,
    updateField,
    listOrphanValues,
    listOrphanSets,
    clearOrphanValues,
    clearOrphanSets,
    clearAllOrphans,
  };
};
